// src/pages/superadmin/ManageUsers.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Card, Table, Button, ButtonGroup,
  Form, InputGroup, Modal, FloatingLabel, Badge, Spinner
} from "react-bootstrap";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import {
  collection, doc, setDoc, updateDoc, deleteDoc, getDocs,
  onSnapshot, orderBy, query, serverTimestamp
} from "firebase/firestore";
import { db } from "../../services/firebase";

import { getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";

/* ---------------- helpers ---------------- */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmt = (d, f = "DD MMM YYYY") => (d && dayjs(d).isValid() ? dayjs(d).format(f) : "—");
const initials = (name = "") => name.trim().split(/\s+/, 2).map(p => p[0]?.toUpperCase() || "").join("");

const randomPass = (len = 12) =>
  Array.from(crypto.getRandomValues(new Uint32Array(len)))
    .map((n) => "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%".charAt(n % 65))
    .join("");

// Normalize any incoming user doc (top-level or legacy) into a consistent shape
// Now supports string status ("active"/"inactive") as source of truth, with boolean active as fallback.
// Replace your normalizeUser with this:
const normalizeUser = (id, data = {}, overrides = {}) => {
  const roleRaw = data.role || overrides.role || "";
  const role = String(roleRaw || "").toLowerCase();

  // prefer string 'status'; fallback to legacy boolean 'active'
  const statusStr = (data.status ?? overrides.status ?? "").toString().toLowerCase().trim();
  const status =
    statusStr === "active" || statusStr === "inactive"
      ? statusStr
      : (data.active === false ? "inactive" : "active");

  return {
    id,
    displayName: data.displayName || overrides.displayName || "",
    email: (data.email || overrides.email || "").toLowerCase(),
    role,
    schoolId: data.schoolId || overrides.schoolId || "",
    className: data.className || "",
    section: data.section || "",
    assignedClasses: Array.isArray(data.assignedClasses) ? data.assignedClasses : [],
    status,                  // ← source of truth
    active: status === "active",
    _createdAt: toDate(data.createdAt) || toDate(overrides.createdAt) || null,
  };
};


// Create Auth user on a secondary app so current session doesn’t get clobbered
async function createAuthUser({ email, password, displayName }) {
  const apps = getApps();
  const primary = apps[0];
  const options = primary?.options;

  let secondaryAuth;
  if (options) {
    const secondaryApp =
      apps.find((a) => a.name === "secondary") || initializeApp(options, "secondary");
    secondaryAuth = getAuth(secondaryApp);
  } else {
    secondaryAuth = getAuth();
  }

  const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
  try { if (displayName) await updateProfile(cred.user, { displayName }); } catch {}
  try { await signOut(secondaryAuth); } catch {}
  return cred.user; // { uid, email }
}

/* ---------------- modal defaults ---------------- */
const DEFAULT = {
  displayName: "",
  email: "",
  role: "student",            // student | teacher | schooladmin | superadmin
  schoolId: "",
  // student-only:
  className: "",
  section: "",
  // teacher-only:
  assignedClasses: [],        // [{className, section}]
  active: true,               // UI toggle, persisted as both boolean & string status
};

/* ---------------- modal ---------------- */
function UserFormModal({ show, onHide, initial, onSave, schools, classesCatalog }) {
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);

  // class-section picker for teachers
  const [selClass, setSelClass] = useState("");
  const [selSection, setSelSection] = useState("");

  useEffect(() => {
    if (initial) {
      setForm({
        displayName: initial.displayName || "",
        email: initial.email || "",
        role: initial.role || "student",
        schoolId: initial.schoolId || "",
        className: initial.className || "",
        section: initial.section || "",
        assignedClasses: Array.isArray(initial.assignedClasses) ? initial.assignedClasses : [],
        active: initial.status ? String(initial.status).toLowerCase() === "active" : (initial.active !== false),
        id: initial.id || "", // for showing UID
      });
    } else {
      setForm(DEFAULT);
    }
    setSelClass("");
    setSelSection("");
  }, [initial, show]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const classOptions = useMemo(() => {
    if (!form.schoolId) return [];
    return (classesCatalog[form.schoolId] || []).map(c => c.name);
  }, [classesCatalog, form.schoolId]);

  const sectionOptions = useMemo(() => {
    if (!form.schoolId || !selClass) return [];
    const cls = (classesCatalog[form.schoolId] || []).find(c => c.name === selClass);
    return Array.isArray(cls?.sections) ? cls.sections : [];
  }, [classesCatalog, form.schoolId, selClass]);

  const addAssignment = () => {
    if (!selClass) return Swal.fire({ icon: "info", title: "Select a class" });
    const key = `${selClass.toLowerCase()}|${(selSection || "").toLowerCase()}`;
    const exists = (form.assignedClasses || []).some(
      cs => `${String(cs.className).toLowerCase()}|${String(cs.section || "").toLowerCase()}` === key
    );
    if (exists) return Swal.fire({ icon: "info", title: "Already added" });
    setForm(f => ({
      ...f,
      assignedClasses: [...(f.assignedClasses || []), { className: selClass, section: selSection }],
    }));
    setSelClass(""); setSelSection("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.displayName.trim()) return Swal.fire({ icon: "warning", title: "Name is required" });
    if (!form.email.trim()) return Swal.fire({ icon: "warning", title: "Email is required" });
    if (!form.schoolId && form.role !== "superadmin")
      return Swal.fire({ icon: "warning", title: "Pick a school for this user" });

    setSaving(true);
    try {
      const payload = {
        displayName: form.displayName.trim(),
        email: form.email.trim().toLowerCase(),
        role: String(form.role || "").toLowerCase(),
        schoolId: form.role === "superadmin" ? "" : form.schoolId,
        className: form.role === "student" ? (form.className || "") : "",
        section: form.role === "student" ? (form.section || "") : "",
        assignedClasses: form.role === "teacher" ? (form.assignedClasses || []) : [],
        // UI boolean; the saver will persist both string `status` and boolean `active`
        active: !!form.active,
      };
      await onSave(payload);
      onHide?.();
    } finally { setSaving(false); }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{initial ? "Edit User" : "Add User"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            {initial?.id && (
              <Col md={6}>
                <FloatingLabel label="UID">
                  <Form.Control
                    value={initial.id}
                    readOnly
                    onFocus={(e) => e.target.select()}
                  />
                </FloatingLabel>
              </Col>
            )}
            <Col md={initial?.id ? 6 : 6}>
              <FloatingLabel label="Full Name *">
                <Form.Control
                  value={form.displayName}
                  onChange={(e) => change("displayName", e.target.value)}
                  placeholder="User name"
                />
              </FloatingLabel>
            </Col>
            <Col md={6}>
              <FloatingLabel label="Email *">
                <Form.Control
                  type="email"
                  value={form.email}
                  onChange={(e) => change("email", e.target.value)}
                  placeholder="user@school.com"
                />
              </FloatingLabel>
            </Col>

            <Col md={6}>
              <FloatingLabel label="Role *">
                <Form.Select
                  value={form.role}
                  onChange={(e) => change("role", e.target.value)}
                >
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                  <option value="schooladmin">School Admin</option>
                  <option value="superadmin">Super Admin</option>
                </Form.Select>
              </FloatingLabel>
            </Col>

            {form.role !== "superadmin" && (
              <Col md={6}>
                <FloatingLabel label="School *">
                  <Form.Select
                    value={form.schoolId}
                    onChange={(e) => change("schoolId", e.target.value)}
                  >
                    <option value="">Select school</option>
                    {schools.map(s => (
                      <option key={s.id} value={s.id}>{s.name || s.id}</option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Col>
            )}

            {form.role === "student" && (
              <>
                <Col md={6}>
                  <FloatingLabel label="Class">
                    <Form.Select
                      value={form.className}
                      onChange={(e) => change("className", e.target.value)}
                      disabled={!form.schoolId}
                    >
                      <option value="">—</option>
                      {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
                    </Form.Select>
                  </FloatingLabel>
                </Col>
                <Col md={6}>
                  <FloatingLabel label="Section">
                    <Form.Select
                      value={form.section}
                      onChange={(e) => change("section", e.target.value)}
                      disabled={!form.className}
                    >
                      <option value="">—</option>
                      {(classesCatalog[form.schoolId] || [])
                        .find(c => c.name === form.className)?.sections?.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                    </Form.Select>
                  </FloatingLabel>
                </Col>
              </>
            )}

            {form.role === "teacher" && (
              <Col xs={12}>
                <Card className="border-0">
                  <Card.Body className="p-3 border rounded-3">
                    <div className="fw-semibold mb-2">Assigned Classes & Sections</div>
                    <Row className="g-2 align-items-end mb-2">
                      <Col md={5}>
                        <FloatingLabel label="Class">
                          <Form.Select
                            value={selClass}
                            onChange={(e) => { setSelClass(e.target.value); setSelSection(""); }}
                            disabled={!form.schoolId}
                          >
                            <option value="">Select class</option>
                            {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
                          </Form.Select>
                        </FloatingLabel>
                      </Col>
                      <Col md={5}>
                        <FloatingLabel label="Section">
                          <Form.Select
                            value={selSection}
                            onChange={(e) => setSelSection(e.target.value)}
                            disabled={!selClass}
                          >
                            <option value="">All sections</option>
                            {(() => {
                              const cls = (classesCatalog[form.schoolId] || []).find(c => c.name === selClass);
                              return Array.isArray(cls?.sections) ? cls.sections : [];
                            })().map(s => <option key={s} value={s}>{s}</option>)}
                          </Form.Select>
                        </FloatingLabel>
                      </Col>
                      <Col md="auto">
                        <Button type="button" onClick={addAssignment}>Add</Button>
                      </Col>
                    </Row>

                    {(form.assignedClasses || []).length ? (
                      <div className="d-flex flex-wrap gap-2">
                        {form.assignedClasses.map((cs, idx) => (
                          <Badge bg="light" text="dark" className="border" key={`${cs.className}-${cs.section}-${idx}`}>
                            <span className="me-2">
                              {cs.className}{cs.section ? `-${cs.section}` : ""}
                            </span>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              className="py-0 px-1"
                              onClick={() => {
                                setForm(f => ({
                                  ...f,
                                  assignedClasses: f.assignedClasses.filter((_, i) => i !== idx),
                                }));
                              }}
                              title="Remove"
                            >
                              <i className="bi bi-x-lg" />
                            </Button>
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted small">No class-section assigned yet.</div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            )}

            <Col md={6}>
              <FloatingLabel label="Status">
                <Form.Select
                  value={form.active ? "true" : "false"}
                  onChange={(e) => change("active", e.target.value === "true")}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Form.Select>
              </FloatingLabel>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/* ---------------- main page ---------------- */
export default function ManageUsers() {
  const [loading, setLoading] = useState(true);

  // Primary + legacy streams
  const [usersTop, setUsersTop] = useState([]);            // top-level /users
  const [usersLegacyTeachers, setUsersLegacyTeachers] = useState([]); // /teachers (legacy)
  const [usersSchoolScoped, setUsersSchoolScoped] = useState([]);     // /schools/*/users

  const [schools, setSchools] = useState([]);
  const [classesCatalog, setClassesCatalog] = useState({}); // { [schoolId]: [{id,name,sections}] }

  // filters
  const [qText, setQText] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [schoolFilter, setSchoolFilter] = useState("all");

  // modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const searchRef = useRef(null);

  // ---- live top-level users (no orderBy to avoid excluding docs missing createdAt) ----
  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, "users"),
      (snap) => {
        const list = snap.docs.map(d => normalizeUser(d.id, d.data()));
        setUsersTop(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  // ---- load schools once (then we’ll subscribe to classes and school-scoped users) ----
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "schools"));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSchools(list);
    })();
  }, []);

  // ---- live classes per school (for modal pickers) ----
  useEffect(() => {
    const unsubs = [];
    schools.forEach((s) => {
      const qy = query(collection(db, "schools", s.id, "classes"), orderBy("name", "asc"));
      const unsub = onSnapshot(qy, (snap) => {
        const arr = snap.docs.map(d => {
          const data = d.data() || {};
          return { id: d.id, name: data.name || d.id, sections: Array.isArray(data.sections) ? data.sections : [] };
        });
        setClassesCatalog(prev => ({ ...prev, [s.id]: arr }));
      });
      unsubs.push(unsub);
    });
    return () => unsubs.forEach(u => u());
  }, [schools]);

  // ---- legacy stream: top-level /teachers collection (if exists) ----
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "teachers"),
      (snap) => {
        const list = snap.docs.map(d => normalizeUser(d.id, d.data(), { role: "teacher" }));
        setUsersLegacyTeachers(list);
      },
      () => setUsersLegacyTeachers([])
    );
    return () => unsub();
  }, []);

  // ---- legacy stream: /schools/*/users subcollections ----
  useEffect(() => {
    const unsubs = [];
    schools.forEach((s) => {
      const colRef = collection(db, "schools", s.id, "users");
      const unsub = onSnapshot(colRef, (snap) => {
        const list = snap.docs.map(d => normalizeUser(d.id, d.data(), { schoolId: s.id }));
        // We store a flattened union for all schools
        setUsersSchoolScoped(prev => {
          // replace all entries from this schoolId
          const others = prev.filter(u => u.schoolId !== s.id);
          return [...others, ...list];
        });
      }, () => {
        // if unsubscribing/err, drop entries from this school
        setUsersSchoolScoped(prev => prev.filter(u => u.schoolId !== s.id));
      });
      unsubs.push(unsub);
    });
    return () => unsubs.forEach(u => u());
  }, [schools]);

  // ---- merged users (by id; top-level wins, then school-scoped, then legacy teachers) ----
const users = useMemo(() => {
  const byId = new Map();

  const put = (u) => {
    if (!u?.id) return;
    const existing = byId.get(u.id);
    if (!existing) {
      byId.set(u.id, u);
    } else {
      // Later 'u' should WIN (top-level processed last).
      byId.set(u.id, {
        ...existing,
        ...u,
        // For key fields, prefer 'u' first, then 'existing'
        email: u.email || existing.email || "",
        role: u.role || existing.role || "",
        schoolId: (u.schoolId ?? existing.schoolId) ?? "",
        // Status: prefer 'u' (newer) then existing; default 'active'
        status: (u.status || existing.status || "active").toLowerCase(),
        active: ((u.status || existing.status || "active").toLowerCase() === "active"),
        // Keep earliest createdAt if you want, or prefer existing
        _createdAt: existing._createdAt || u._createdAt || null,
      });
    }
  };

  usersSchoolScoped.forEach(put);
  usersLegacyTeachers.forEach(put);
  usersTop.forEach(put); // ← top-level last, so it wins

  return Array.from(byId.values()).sort((a, b) => {
    const ta = a._createdAt ? +a._createdAt : 0;
    const tb = b._createdAt ? +b._createdAt : 0;
    return tb - ta;
  });
}, [usersTop, usersLegacyTeachers, usersSchoolScoped]);
  

  // ---- filtering (case-insensitive role) ----
  const filtered = useMemo(() => {
    const q = qText.trim().toLowerCase();
    return users.filter(u => {
      if (roleFilter !== "all" && String(u.role || "").toLowerCase() !== roleFilter) return false;
      if (schoolFilter !== "all" && (u.schoolId || "") !== schoolFilter) return false;
      if (q) {
        const hay = [
          u.displayName, u.email, u.role, u.schoolId,
          u.className, u.section, u.status
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, qText, roleFilter, schoolFilter]);

  const openAdd = () => { setEditing(null); setShowModal(true); };
  const openEdit = (u) => { setEditing(u); setShowModal(true); };

  const handleSendReset = async (row) => {
    if (!row?.email) return Swal.fire({ icon: "info", title: "No email on file" });
    try {
      await sendPasswordResetEmail(getAuth(), row.email);
      Swal.fire({ icon: "success", title: "Reset email sent", text: `Sent to ${row.email}`, timer: 1600, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Could not send reset email", text: e?.message || "Try again" });
    }
  };

  const save = async (payload) => {
    // Compose status string + legacy boolean for writes
    const statusString = (payload.active ? "active" : "inactive");

    // New user -> create auth (with temp password) + setDoc(uid)
    if (!editing) {
      const tempPassword = randomPass(10);
      try {
        const authUser = await createAuthUser({
          email: payload.email,
          password: tempPassword,
          displayName: payload.displayName,
        });

        await setDoc(doc(db, "users", authUser.uid), {
          role: payload.role,
          schoolId: payload.schoolId || "",
          email: authUser.email,
          displayName: payload.displayName || "",
          className: payload.role === "student" ? (payload.className || "") : "",
          section: payload.role === "student" ? (payload.section || "") : "",
          assignedClasses: payload.role === "teacher" ? (payload.assignedClasses || []) : [],
          // Write both for compatibility:
          status: statusString,
          active: payload.active ?? true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }, { merge: true });

        await Swal.fire({
          icon: "success",
          title: "User created",
          html: `
            <div style="text-align:left;line-height:1.55">
              <p><strong>Email:</strong> ${authUser.email}</p>
              <p><strong>Temporary Password:</strong> <code>${tempPassword}</code></p>
              <p class="text-muted">Share credentials; the user should change the password after first login.</p>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: "Copy",
          cancelButtonText: "Close",
          preConfirm: () => {
            const t = `Email: ${authUser.email}\nTemp Password: ${tempPassword}`;
            return navigator.clipboard.writeText(t).catch(() => {});
          },
        });
      } catch (e) {
        await Swal.fire({ icon: "error", title: "Auth creation failed", text: e?.message || "Try again" });
      }
      return;
    }

    // Existing user -> update Firestore doc (top-level users)
    try {
      await updateDoc(doc(db, "users", editing.id), {
        role: String(payload.role || "").toLowerCase(),
        schoolId: payload.role === "superadmin" ? "" : (payload.schoolId || ""),
        email: payload.email, // note: this does NOT change Auth email
        displayName: payload.displayName || "",
        className: payload.role === "student" ? (payload.className || "") : "",
        section: payload.role === "student" ? (payload.section || "") : "",
        assignedClasses: payload.role === "teacher" ? (payload.assignedClasses || []) : [],
        // keep both fields up to date
        status: statusString,
        active: payload.active ?? true,
        updatedAt: serverTimestamp(),
      });
      Swal.fire({ icon: "success", title: "Updated", timer: 900, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Update failed", text: e?.message || "Try again" });
    }
  };

  const removeRow = async (row) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Delete user?",
      text: `Remove "${row.displayName}" (${row.role}).`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!res.isConfirmed) return;
    try {
      await deleteDoc(doc(db, "users", row.id));
      Swal.fire({ icon: "success", title: "Deleted", timer: 900, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Delete failed", text: e?.message || "Try again" });
    }
  };

  const exportCSV = () => {
    const data = filtered.map((u) => ({
      uid: u.id,
      name: u.displayName || "",
      email: u.email || "",
      role: u.role || "",
      schoolId: u.schoolId || "",
      class: u.className || "",
      section: u.section || "",
      assignedClasses: (u.assignedClasses || [])
        .map(cs => `${cs.className}${cs.section ? `-${cs.section}` : ""}`).join(", "),
      status: u.status || ((u.active !== false) ? "active" : "inactive"),
      createdAt: fmt(u._createdAt, "YYYY-MM-DD"),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "users");
    XLSX.writeFile(wb, "users_export.csv");
  };

  return (
    <Container fluid className="p-0">
      {/* Header */}
      <Row className="mb-3 g-2 align-items-center">
        <Col>
          <h5 className="mb-0">Manage Users</h5>
          <small className="text-muted">Create, edit, and manage users across schools</small>
        </Col>
        <Col className="text-end">
          <ButtonGroup>
            <Button variant="outline-secondary" onClick={exportCSV}>
              <i className="bi bi-download me-1" /> Export CSV
            </Button>
            <Button onClick={() => { setEditing(null); setShowModal(true); }}>
              <i className="bi bi-plus-lg me-1" /> Add User
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="p-3 mb-3">
        <Row className="g-2 align-items-end">
          <Col md={5}>
            <Form.Label className="mb-1">Search</Form.Label>
            <InputGroup>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control
                ref={searchRef}
                placeholder="Name, email, role, school…"
                value={qText}
                onChange={(e) => setQText(e.target.value)}
              />
              {qText && (
                <Button variant="outline-secondary" onClick={() => setQText("")}>Clear</Button>
              )}
            </InputGroup>
          </Col>
          <Col md={3}>
            <Form.Label className="mb-1">Role</Form.Label>
            <Form.Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              {["all","student","teacher","schooladmin","superadmin"].map(r => (
                <option key={r} value={r}>{r === "all" ? "All" : r}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="mb-1">School</Form.Label>
            <Form.Select value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)}>
              <option value="all">All</option>
              {schools.map(s => (
                <option key={s.id} value={s.id}>{s.name || s.id}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={1} className="text-end">
            <Badge bg="light" text="dark" className="border">
              {filtered.length} of {users.length}
            </Badge>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card className="p-3">
        <div className="table-responsive">
          <Table hover className="align-middle">
            <thead>
              <tr>
                <th className="d-none d-sm-table-cell">Avatar</th>
                <th>Name</th>
                <th className="d-none d-lg-table-cell">Email</th>
                <th>Role</th>
                <th className="d-none d-md-table-cell">School</th>
                <th className="d-none d-lg-table-cell">Class</th>
                <th className="d-none d-lg-table-cell">Section</th>
                <th className="d-none d-xl-table-cell">Assigned (Teacher)</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="text-center py-5">
                    <Spinner animation="border" size="sm" /> Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center text-muted py-4">No users found</td>
                </tr>
              ) : (
                filtered.map((u) => {
                  const status = (u.status || ((u.active !== false) ? "active" : "inactive")).toLowerCase();
                  const isActive = status === "active";
                  return (
                    <tr key={u.id}>
                      <td className="d-none d-sm-table-cell" style={{ width: 56 }}>
                        <div
                          className="d-inline-flex justify-content-center align-items-center bg-light border"
                          style={{ height: 36, width: 36, borderRadius: 10, fontSize: 12, fontWeight: 600, color: "#666" }}
                          title={u.displayName}
                        >
                          {initials(u.displayName || u.email || "U")}
                        </div>
                      </td>
                      <td>
                        <div className="fw-semibold text-truncate" style={{ maxWidth: 220 }}>
                          {u.displayName || "—"}
                        </div>
                        <div className="text-muted small">{u.id}</div>
                      </td>
                      <td className="d-none d-lg-table-cell">{u.email || "—"}</td>
                      <td className="text-capitalize">{u.role || "—"}</td>
                      <td className="d-none d-md-table-cell">
                        {u.role === "superadmin" ? "—" : (schools.find(s => s.id === u.schoolId)?.name || u.schoolId || "—")}
                      </td>
                      <td className="d-none d-lg-table-cell">{u.className || "—"}</td>
                      <td className="d-none d-lg-table-cell">{u.section || "—"}</td>
                      <td className="d-none d-xl-table-cell">
                        {(u.assignedClasses || []).length ? (
                          <div className="d-flex flex-wrap gap-1">
                            {u.assignedClasses.map((cs, i) => (
                              <Badge bg="light" text="dark" className="border" key={`${u.id}-cs-${i}`}>
                                {cs.className}{cs.section ? `-${cs.section}` : ""}
                              </Badge>
                            ))}
                          </div>
                        ) : "—"}
                      </td>
                      <td>
                        {isActive ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>}
                      </td>
                      <td className="text-end">
                        {/* Copy UID */}
                        <Button
                          size="sm"
                          variant="outline-dark"
                          className="me-2"
                          title="Copy UID"
                          onClick={() => navigator.clipboard.writeText(u.id).catch(() => {})}
                        >
                          <i className="bi bi-fingerprint" />
                        </Button>

                        {u.email && (
                          <>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="me-2"
                              title="Copy email"
                              onClick={() => navigator.clipboard.writeText(u.email).catch(() => {})}
                            >
                              <i className="bi bi-clipboard" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-success"
                              className="me-2"
                              title="Send password reset email"
                              onClick={() => handleSendReset(u)}
                            >
                              <i className="bi bi-envelope-paper" />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="outline-secondary" className="me-2" title="Edit" onClick={() => openEdit(u)}>
                          <i className="bi bi-pencil" />
                        </Button>
                        <Button size="sm" variant="outline-danger" title="Delete" onClick={() => removeRow(u)}>
                          <i className="bi bi-trash" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Modal */}
      <UserFormModal
        show={showModal}
        onHide={() => setShowModal(false)}
        initial={editing}
        onSave={save}
        schools={schools}
        classesCatalog={classesCatalog}
      />

      <style>{`
        .card { border-radius: 14px; }
        .form-control, .form-select { border-radius: 10px; }
      `}</style>
    </Container>
  );
}
